import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import {
  User,
  AuthContextType,
  Permission,
  CargoFranquia,
  AuthError,
  TokenResponse,
  LoginResponse,
} from "../types/auth";
import { authService, setAuthHeader } from "../services/auth";
import { tokenService } from "../services/tokenService";

// Criação do contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Props do provider
interface AuthProviderProps {
  children: ReactNode;
}

// Provider do contexto de autenticação
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Computed properties
  const isAuthenticated = !!user && !!tokenService.getToken();

  const logout = useCallback((): void => {
    tokenService.removeToken();
    setUser(null);
  }, []);

  const refreshProfile = useCallback(async (): Promise<void> => {
    try {
      const profile = await authService.getProfile();
      setUser(profile);
    } catch (error) {
      console.error("Erro ao buscar perfil:", error);
      // Sem toast aqui de propósito: refreshProfile também roda no fluxo
      // normal de initAuth logo abaixo, que já avisa o usuário quando o
      // logout forçado é por sessão expirada/inválida — evita toast duplicado.
      logout();
    }
  }, [logout]);

  useEffect(() => {
    const initAuth = async () => {
      const token = tokenService.getToken();
      if (token) {
        try {
          setAuthHeader(token);
          await refreshProfile();
        } catch (error) {
          console.error("Falha na autenticação inicial:", error);
          // Usuário é ejetado pro login sem isso — ao menos explica o porquê
          // em vez de simplesmente "sumir" da tela em que estava.
          toast.error("Sua sessão expirou. Faça login novamente.");
          logout();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [logout, refreshProfile]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const loginResponse = await authService.login({ email, password });
      
      const token: TokenResponse = {
        access: loginResponse.access,
        refresh: loginResponse.refresh,
      };

      tokenService.setToken(token);
      setAuthHeader(token);
      setUser(loginResponse.user);

    } catch (error: any) {
      console.error("Erro no login:", error);
      const authError: AuthError = {
        message: error.message || "Erro ao fazer login",
        code: error.code,
        details: error.details,
      };
      throw authError;
    } finally {
      setIsLoading(false);
    }
  };

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;

    if (user.tipo === "TACT") {
      return true;
    }

    // Usuários FRQ - verificar permissões baseadas no cargo
    if (user.tipo === "FRQ" && user.franquia_membership) {
      const cargos = user.franquia_membership.cargos;

      if (cargos.includes("ADMIN")) {
        const systemPermissions = [
          Permission.MANAGE_SYSTEM,
          Permission.VIEW_ALL_FRANQUIAS,
          Permission.MANAGE_CIDADES,
          Permission.MANAGE_AREAS_CIDADE,
        ];
        return !systemPermissions.includes(permission);
      }

      if (cargos.includes("GESTOR")) {
        const restrictedPermissions = [
          Permission.MANAGE_SYSTEM,
          Permission.VIEW_ALL_FRANQUIAS,
          Permission.MANAGE_CIDADES,
          Permission.MANAGE_AREAS_CIDADE,
          Permission.ADD_GESTOR, // Só ADMIN pode adicionar gestores
        ];
        return !restrictedPermissions.includes(permission);
      }

      return checkCargoPermissions(cargos, permission);
    }

    return false;
  };

  const checkCargoPermissions = (
    cargos: CargoFranquia[],
    permission: Permission
  ): boolean => {
    const cargoPermissions: Record<CargoFranquia, Permission[]> = {
      ADMIN: [], // ADMIN é tratado separadamente
      GESTOR: [], // GESTOR é tratado separadamente
      FINANCEIRO: [
        Permission.VIEW_FINANCEIRO,
        Permission.MANAGE_FINANCEIRO,
        Permission.VIEW_DASHBOARD,
      ],
      CADASTRAMENTO: [
        Permission.MANAGE_CADASTROS,
        Permission.VIEW_CADASTROS,
        Permission.VIEW_DASHBOARD,
        Permission.MANAGE_EMPREENDIMENTOS,
        Permission.VIEW_EMPREENDIMENTOS,
      ],
      VENDEDOR: [
        Permission.MANAGE_VENDAS,
        Permission.VIEW_VENDAS,
        Permission.VIEW_DASHBOARD,
        Permission.VIEW_EMPREENDIMENTOS,
      ],
    };

    return cargos.some((cargo) =>
      cargoPermissions[cargo]?.includes(permission)
    );
  };

  const hasRole = (role: CargoFranquia): boolean => {
    if (!user || user.tipo !== "FRQ" || !user.franquia_membership) {
      return false;
    }
    return user.franquia_membership.cargos.includes(role);
  };
  const isTactUser = (): boolean => user?.tipo === "TACT";
  const isFranquiaUser = (): boolean => user?.tipo === "FRQ";
  const getFranquiaId = (): number | null =>
    user?.franquia_membership?.franquia_id || null;
  const getFranquiaNome = (): string | null =>
    user?.franquia_membership?.franquia_nome || null;

  // Valor do contexto
  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshProfile,
    hasPermission,
    hasRole,
    isTactUser,
    isFranquiaUser,
    getFranquiaId,
    getFranquiaNome,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Hook para usar o contexto de autenticação
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

// Hook para verificar permissões (conveniência)
export const usePermission = (permission: Permission): boolean => {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
};

// Hook para verificar cargo (conveniência)
export const useRole = (role: CargoFranquia): boolean => {
  const { hasRole } = useAuth();
  return hasRole(role);
};
